import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ISSUE_KIND_LABEL, ISSUE_RESOLUTION_LABEL, IssueResolution, can, type Issue } from '@torapon/shared';
import { PhotoDialog } from '../components/Photos';
import { useMe } from '../lib/me';
import { useToast } from '../components/ui';
import { api, HttpError } from '../lib/api';
import { useNames } from '../lib/directory';
import { fmtDateTime } from '../lib/format';
import { readNote } from '../lib/fs';
import { useLive } from '../lib/realtime';

/** 問題報告の対応（仕様書「問題報告の対応」）。取り消し・付け替えは配送の一覧から行い、ここでは対応を記録する */
export function IssuesPage() {
  const names = useNames();
  const toast = useToast();
  const [rows, setRows] = useState<Issue[] | null>(null);
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [err, setErr] = useState('');
  const [photosOf, setPhotosOf] = useState<Issue | null>(null);
  const me = useMe();
  const canPhotos = !!me && can(me.role, 'viewPhotos');
  const load = () => api.get<{ issues: Issue[] }>('/v1/office/issues').then(async (r) => {
    setRows(r.issues);
    for (const i of r.issues) { const text = await readNote(i.id).catch(() => ''); setNotes((n) => ({ ...n, [i.id]: text })); }
  }).catch((e) => setErr(e instanceof HttpError ? e.message : '読み込めませんでした'));
  useEffect(() => { void load(); }, []);
  useLive(['issues'], load);
  const resolve = async (id: string, resolution: IssueResolution) => {
    try { await api.post(`/v1/office/issues/${id}/resolve`, { resolution }); toast('対応を記録しました'); await load(); }
    catch (e) { setErr(e instanceof HttpError ? e.message : '記録できませんでした'); }
  };
  return (
    <div className="page" style={{ display: 'grid', gap: 12 }}>
      {err && <p className="err" role="alert">{err}</p>}
      <div className="panel">
        <table>
          <thead><tr><th>報告日時</th><th>ドライバー</th><th>配送番号</th><th>種類</th><th>メモ</th><th>写真</th><th>対応</th></tr></thead>
          <tbody>
            {rows === null ? <tr><td colSpan={7} className="muted">読み込み中…</td></tr>
              : rows.length === 0 ? <tr><td colSpan={7} className="muted">問題の報告はありません</td></tr>
              : rows.map((i) => (
                <tr key={i.id}>
                  <td className="num">{fmtDateTime(i.reportedAt)}</td>
                  <td><Link to={`/drivers/${i.driverId}`}>{names.driver(i.driverId)}</Link></td>
                  <td className="num">{i.deliveryNo}</td>
                  <td>{ISSUE_KIND_LABEL[i.kind]}</td>
                  <td>{notes[i.id] || <span className="muted">—</span>}</td>
                  <td>{i.photos === 0 ? <span className="muted">—</span> : canPhotos ? <button className="btn sm" onClick={() => setPhotosOf(i)}>{i.photos} 枚</button> : `${i.photos} 枚`}</td>
                  <td>{i.resolution ? ISSUE_RESOLUTION_LABEL[i.resolution]
                    : <span style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>{IssueResolution.options.map((r) => <button key={r} className="btn sm" onClick={() => void resolve(i.id, r)}>{ISSUE_RESOLUTION_LABEL[r]}</button>)}</span>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {photosOf && <PhotoDialog title={`${photosOf.deliveryNo} の問題報告の写真`} query={`issueId=${photosOf.id}`} onClose={() => setPhotosOf(null)} />}
    </div>
  );
}
